import type { RowData } from '@tanstack/react-table'
import { Download } from 'lucide-react'

import { Button } from '@/components'
import type {
  Column,
  Row,
  Table as TableType,
} from '@/components/ui/table/types'

interface TableExportProps<TData extends RowData> {
  table: TableType<TData>
  filename?: string
}

export function TableExport<TData extends RowData>({
  table,
  filename = 'transactions',
}: TableExportProps<TData>) {
  const handleExport = () => {
    if (!table) return

    // Filtered and sorted like the screen, before pagination.
    const rows = table.getPrePaginatedRowModel().rows
    const columns = table.getAllColumns().filter((col: Column<TData>) => {
      if (!col.getIsVisible()) return false
      if (col.id === 'select' || col.id === 'tags') return false
      const header = col.columnDef.header
      return typeof header === 'string'
    })

    const headers = columns
      .map((col: Column<TData>) => col.columnDef.header)
      .join(',')

    const formatValue = (value: unknown): string => {
      if (value === null || value === undefined) return ''

      const stringValue = String(value)
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    }

    const exportValue = (row: Row<TData>, col: Column<TData>): unknown => {
      const value = row.getValue(col.id)
      const toLabel = col.columnDef.meta?.exportValue
      return toLabel && value != null && value !== '' ? toLabel(value) : value
    }

    const csvRows = rows.map((row: Row<TData>) => {
      return columns
        .map((col: Column<TData>) => formatValue(exportValue(row, col)))
        .join(',')
    })

    const csv = [headers, ...csvRows].join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${filename}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Button
      theme="outline"
      size="sm"
      className="h-8"
      onClick={handleExport}
      disabled={!table}
    >
      <Download className="h-4 w-4" />
      Export
    </Button>
  )
}
